"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.recordKicadBusAliases = recordKicadBusAliases;
exports.recordKicadBusSheet = recordKicadBusSheet;
exports.recordKicadNetDrivers = recordKicadNetDrivers;
exports.resolveKicadHierarchyNets = resolveKicadHierarchyNets;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const RefableNet_1 = require("./RefableNet");
const KicadNetNamingStrategy_1 = require("./schematic/KicadNetNamingStrategy");
const KicadBusTopology_1 = require("./schematic/KicadBusTopology");
var KicadHierarchyProcessingResultType;
(function (KicadHierarchyProcessingResultType) {
    KicadHierarchyProcessingResultType["MissingChildDesign"] = "kicad-hierarchy-missing-child-design";
    KicadHierarchyProcessingResultType["MissingChildPort"] = "kicad-hierarchy-missing-child-port";
    KicadHierarchyProcessingResultType["MissingParentNet"] = "kicad-hierarchy-missing-parent-net";
    KicadHierarchyProcessingResultType["MissingChildNet"] = "kicad-hierarchy-missing-child-net";
    KicadHierarchyProcessingResultType["MissingSheetSymbol"] = "kicad-hierarchy-missing-sheet-symbol";
})(KicadHierarchyProcessingResultType || (KicadHierarchyProcessingResultType = {}));
const netDrivers = new WeakMap();
const projectBusAliases = new WeakMap();
const designBusComponents = new WeakMap();
function getSheetDepth(sheetPath) {
    return sheetPath.split('/').filter(Boolean).length;
}
function recordKicadBusAliases(project, aliases, sheetPath) {
    const projectAliases = projectBusAliases.get(project) ?? new Map();
    projectAliases.set(sheetPath, aliases);
    projectBusAliases.set(project, projectAliases);
}
function recordKicadBusSheet(design, components) {
    designBusComponents.set(design, components);
}
var KicadBusDriverPriority;
(function (KicadBusDriverPriority) {
    KicadBusDriverPriority[KicadBusDriverPriority["SheetPin"] = 1] = "SheetPin";
    KicadBusDriverPriority[KicadBusDriverPriority["HierarchicalLabel"] = 2] = "HierarchicalLabel";
    KicadBusDriverPriority[KicadBusDriverPriority["LocalLabel"] = 3] = "LocalLabel";
})(KicadBusDriverPriority || (KicadBusDriverPriority = {}));
const KICAD_BUS_MEMBER_PRIORITY = 4.5;
class KicadBusHierarchyResolver {
    constructor(project, netParents, netIndexes) {
        this.project = project;
        this.netParents = netParents;
        this.netIndexes = netIndexes;
        this.slots = new Map();
        this.slotParents = new Map();
        this.sheetPaths = new Map();
        this.localLabels = new Map();
        this.aliasesBySheetPath = projectBusAliases.get(project) ?? new Map();
        this.scalarExitWinners = this.findScalarExitWinners();
        this.indexSheetLabels();
    }
    resolve() {
        this.buildSlots();
        this.joinHierarchySlots();
        for (const group of this.groupSlots().values()) {
            const nets = new Set(group.flatMap((slot) => [...slot.nets]));
            const winner = group
                .flatMap((slot) => slot.drivers)
                .sort((first, second) => this.compareDrivers(first, second))[0];
            if (!winner || nets.size === 0) {
                continue;
            }
            const [firstNet, ...otherNets] = [...nets];
            for (const net of otherNets) {
                unionNets(this.netParents, firstNet, net);
            }
            for (const net of nets) {
                const candidates = netDrivers.get(net) ?? [];
                candidates.push({
                    label: winner.label,
                    priority: KICAD_BUS_MEMBER_PRIORITY,
                    type: 'bus',
                    hierarchyDepth: winner.hierarchyDepth,
                    sheetPath: '/',
                });
                netDrivers.set(net, candidates);
            }
        }
    }
    buildSlots() {
        for (const [design] of this.netIndexes) {
            for (const component of designBusComponents.get(design) ?? []) {
                for (const driver of component.drivers) {
                    for (const member of this.expandBus(design, driver.text)) {
                        const slot = this.getOrCreateSlot(design, component.id, member.key);
                        slot.drivers.push(this.createDriver(design, driver, member));
                        for (const net of this.localLabels.get(design)?.get(member.escapedName) ?? []) {
                            if (!this.isScalarExitOwnedByOtherLabel(design, net, member)) {
                                slot.nets.add(net);
                            }
                        }
                    }
                }
            }
        }
    }
    joinHierarchySlots() {
        for (const parentDesign of this.netIndexes.keys()) {
            for (const drawing of Object.values(parentDesign.drawings).flat()) {
                if (drawing.type !== 'schref' || !drawing.designId) {
                    continue;
                }
                const childDesign = this.project.designs[drawing.designId];
                if (!isSchematicDesign(childDesign)) {
                    continue;
                }
                const parentDrivers = (designBusComponents.get(parentDesign) ?? [])
                    .flatMap((component) => component.drivers)
                    .filter((driver) => driver.type === KicadBusTopology_1.KicadBusDriverType.SheetPin && driver.moduleRef === drawing.drawModule.ref);
                const childDrivers = (designBusComponents.get(childDesign) ?? [])
                    .flatMap((component) => component.drivers)
                    .filter((driver) => driver.type === KicadBusTopology_1.KicadBusDriverType.HierarchicalLabel);
                for (const parentDriver of parentDrivers) {
                    for (const childDriver of childDrivers) {
                        if (parentDriver.text !== childDriver.text) {
                            continue;
                        }
                        for (const member of this.expandBus(parentDesign, parentDriver.text)) {
                            const parentSlot = this.slots.get(this.slotId(parentDesign, parentDriver.componentId, member.key));
                            const childSlot = this.slots.get(this.slotId(childDesign, childDriver.componentId, member.key));
                            if (parentSlot && childSlot) {
                                this.unionSlots(parentSlot, childSlot);
                            }
                        }
                    }
                }
            }
        }
    }
    expandBus(design, text) {
        const expression = KicadBusTopology_1.KicadBusTopology.parseBusLabel(text);
        if (!expression) {
            return [];
        }
        const members = [];
        const sourceMembers = expression.type === KicadBusTopology_1.KicadBusExpressionType.Group ?
            (this.getAliasMembers(design, expression.members.join(' ')) ?? expression.members)
            : expression.members;
        for (const sourceMember of sourceMembers) {
            const nested = KicadBusTopology_1.KicadBusTopology.parseBusLabel(sourceMember);
            const memberNames = nested ? nested.members : [sourceMember];
            for (const member of memberNames) {
                const nestedName = nested?.type === KicadBusTopology_1.KicadBusExpressionType.Vector ? `${nested.prefix}${member}` : member;
                const name = expression.type === KicadBusTopology_1.KicadBusExpressionType.Vector ? `${expression.prefix}${member}`
                    : expression.prefix.length > 0 ? `${expression.prefix}.${nestedName}`
                        : nestedName;
                const key = expression.type === KicadBusTopology_1.KicadBusExpressionType.Vector ? member
                    : nested?.type === KicadBusTopology_1.KicadBusExpressionType.Vector ? nestedName
                        : member;
                members.push({ key, name, escapedName: KicadNetNamingStrategy_1.KicadNetNamingStrategy.escapeNetName(name) });
            }
        }
        return members;
    }
    getAliasMembers(design, aliasName) {
        const candidates = [...this.aliasesBySheetPath].filter(([, aliases]) => aliases.has(aliasName));
        const own = this.getDesignSheetPath(design) === '/' ?
            undefined
            : candidates.find(([path]) => path === this.getDesignSheetPath(design));
        if (own) {
            return own[1].get(aliasName);
        }
        candidates.sort(([first], [second]) => {
            const depthDifference = getSheetDepth(second) - getSheetDepth(first);
            return (depthDifference !== 0 ? depthDifference
                : first < second ? -1
                    : first > second ? 1
                        : 0);
        });
        return candidates[0]?.[1].get(aliasName);
    }
    isScalarExitOwnedByOtherLabel(design, net, member) {
        const winner = this.scalarExitWinners.get(net);
        if (winner?.type === 'local_label') {
            return winner.label !== this.getDesignSheetPath(design) + member.escapedName;
        }
        if (winner?.type === 'global' || winner?.type === 'power_pin') {
            return !(netDrivers.get(net) ?? []).some((candidate) => candidate.type === 'global' || candidate.type === 'power_pin');
        }
        return false;
    }
    getOrCreateSlot(design, componentId, memberKey) {
        const id = this.slotId(design, componentId, memberKey);
        let slot = this.slots.get(id);
        if (!slot) {
            slot = { id, design, componentId, memberKey, nets: new Set(), drivers: [] };
            this.slots.set(id, slot);
            this.slotParents.set(slot, slot);
        }
        return slot;
    }
    slotId(design, componentId, memberKey) {
        return `${design.designId}\0${componentId}\0${memberKey}`;
    }
    createDriver(design, driver, member) {
        const sheetPath = this.getDesignSheetPath(design);
        return {
            label: sheetPath + KicadNetNamingStrategy_1.KicadNetNamingStrategy.escapeNetName(member.name),
            priority: this.busDriverPriority(driver.type),
            hierarchyDepth: sheetPath.split('/').filter(Boolean).length,
        };
    }
    busDriverPriority(type) {
        switch (type) {
            case KicadBusTopology_1.KicadBusDriverType.LocalLabel:
                return KicadBusDriverPriority.LocalLabel;
            case KicadBusTopology_1.KicadBusDriverType.HierarchicalLabel:
                return KicadBusDriverPriority.HierarchicalLabel;
            case KicadBusTopology_1.KicadBusDriverType.SheetPin:
                return KicadBusDriverPriority.SheetPin;
        }
    }
    getDesignSheetPath(design) {
        return this.sheetPaths.get(design) ?? '/';
    }
    findScalarExitWinners() {
        const exits = new Map();
        for (const group of groupNets(this.netParents).values()) {
            const winner = getGroupWinners(new Map([[group[0], group]])).get(group[0]);
            if (!winner) {
                continue;
            }
            for (const net of group) {
                const hasChildHierarchicalLabel = (netDrivers.get(net) ?? []).some((candidate) => candidate.hierarchyDepth > 0 && candidate.type === 'hier_label');
                if (hasChildHierarchicalLabel) {
                    exits.set(net, winner);
                }
            }
        }
        return exits;
    }
    indexSheetLabels() {
        for (const [design, netIndex] of this.netIndexes) {
            const labels = new Map();
            let sheetPath = '/';
            for (const net of netIndex.values()) {
                const path = netDrivers.get(net)?.[0]?.sheetPath;
                if (path !== undefined) {
                    sheetPath = path;
                }
                for (const label of net.netLabels ?? []) {
                    if (label.type !== 'local_label') {
                        continue;
                    }
                    const authored = label.label.startsWith(sheetPath) ? label.label.slice(sheetPath.length) : label.label;
                    const nets = labels.get(authored) ?? new Set();
                    nets.add(net);
                    labels.set(authored, nets);
                }
            }
            this.sheetPaths.set(design, sheetPath);
            this.localLabels.set(design, labels);
        }
    }
    findSlot(slot) {
        const parent = this.slotParents.get(slot);
        if (!parent || parent === slot) {
            return slot;
        }
        const root = this.findSlot(parent);
        this.slotParents.set(slot, root);
        return root;
    }
    unionSlots(first, second) {
        const firstRoot = this.findSlot(first);
        const secondRoot = this.findSlot(second);
        if (firstRoot !== secondRoot) {
            this.slotParents.set(secondRoot, firstRoot);
        }
    }
    groupSlots() {
        const groups = new Map();
        for (const slot of this.slots.values()) {
            const root = this.findSlot(slot);
            const group = groups.get(root) ?? [];
            group.push(slot);
            groups.set(root, group);
        }
        return groups;
    }
    compareDrivers(first, second) {
        if (first.priority !== second.priority) {
            return second.priority - first.priority;
        }
        if (first.hierarchyDepth !== second.hierarchyDepth) {
            return first.hierarchyDepth - second.hierarchyDepth;
        }
        return (first.label < second.label ? -1
            : first.label > second.label ? 1
                : 0);
    }
}
function recordKicadNetDrivers(netInfo, net, netPath) {
    const hierarchyDepth = netPath.split('/').filter(Boolean).length;
    const candidates = [];
    for (const label of net.labels) {
        if (!label.canNameNet) {
            continue;
        }
        const formattedLabel = label.labelType === 'hier_label' ?
            netPath + KicadNetNamingStrategy_1.KicadNetNamingStrategy.escapeNetName(label.originalLabel)
            : (label.formattedLabel ?? label.originalLabel);
        candidates.push({
            label: formattedLabel,
            priority: KicadNetNamingStrategy_1.KicadNetNamingStrategy.getLabelPriority(label.labelType),
            type: label.labelType,
            hierarchyDepth,
            sheetPath: netPath,
        });
    }
    for (const connection of net.connections) {
        const driver = connection.connection.appliesNetName;
        if (!driver) {
            continue;
        }
        candidates.push({
            label: driver.label,
            priority: KicadNetNamingStrategy_1.KicadNetNamingStrategy.getLabelPriority(driver.labelType),
            type: driver.labelType,
            hierarchyDepth,
            sheetPath: netPath,
        });
    }
    const resolvedName = String(netInfo.name);
    for (const candidate of candidates) {
        if (candidate.type === 'pin_hint') {
            candidate.label = resolvedName;
        }
    }
    if (resolvedName.startsWith('unconnected-')) {
        for (const candidate of candidates) {
            if (candidate.type === 'pin') {
                candidate.label = resolvedName;
            }
        }
    }
    netDrivers.set(netInfo, candidates);
}
function resolveKicadHierarchyNets(project) {
    const parent = new Map();
    const netIndexes = new Map();
    const netOwners = new WeakMap();
    const unmatchedSheetPins = [];
    const matchedSheetPins = new Map();
    for (const design of Object.values(project.designs)) {
        if (!isSchematicDesign(design)) {
            continue;
        }
        const netIndex = indexDesignNets(design);
        netIndexes.set(design, netIndex);
        for (const net of netIndex.values()) {
            netOwners.set(net, design);
            if (netDrivers.has(net)) {
                parent.set(net, net);
            }
        }
    }
    for (const parentDesign of Object.values(project.designs)) {
        if (!isSchematicDesign(parentDesign)) {
            continue;
        }
        const parentNetIndex = netIndexes.get(parentDesign);
        if (!parentNetIndex) {
            continue;
        }
        for (const drawing of Object.values(parentDesign.drawings).flat()) {
            if (drawing.type !== 'schref') {
                continue;
            }
            const module = drawing.drawModule;
            const component = project.components[module.componentRef];
            const symbol = component?.symbols.find((candidate) => candidate.ref === module.footprintRef);
            if (!symbol) {
                recordHierarchySheetWarning(project, KicadHierarchyProcessingResultType.MissingSheetSymbol, drawing.name, 'sheet symbol component or symbol definition');
                continue;
            }
            const childDesign = drawing.designId ? project.designs[drawing.designId] : undefined;
            if (!isSchematicDesign(childDesign)) {
                recordHierarchySheetWarning(project, KicadHierarchyProcessingResultType.MissingChildDesign, drawing.name, 'child schematic design');
                for (const pin of symbol.pins) {
                    recordUnmatchedSheetPin(unmatchedSheetPins, module.pinNets[pin.ref], parentNetIndex, String(pin.name), parentDesign);
                }
                continue;
            }
            for (const pin of symbol.pins) {
                const pinName = String(pin.name);
                if (KicadBusTopology_1.KicadBusTopology.isBusLabel(pinName)) {
                    continue;
                }
                const parentNetName = module.pinNets[pin.ref];
                const parentNet = parentNetName ? parentNetIndex.get(String(parentNetName)) : undefined;
                if (!parentNet) {
                    recordHierarchyWarning(project, KicadHierarchyProcessingResultType.MissingParentNet, drawing.name, pinName, 'parent sheet-pin net');
                }
                const childPort = childDesign.ports[pinName];
                if (!childPort) {
                    recordHierarchyWarning(project, KicadHierarchyProcessingResultType.MissingChildPort, drawing.name, pinName, 'child hierarchical port');
                    recordUnmatchedSheetPin(unmatchedSheetPins, parentNetName, parentNetIndex, pinName, parentDesign);
                    continue;
                }
                if (!parentNet) {
                    continue;
                }
                const matchedPins = matchedSheetPins.get(parentDesign) ?? [];
                matchedPins.push({
                    net: parentNet,
                    name: getNetSheetPath(parentNet) + KicadNetNamingStrategy_1.KicadNetNamingStrategy.escapeNetName(pinName),
                    isSoleSheetPinDriver: (netDrivers.get(parentNet) ?? []).filter((candidate) => candidate.type === 'sheet_pin').length === 1,
                });
                matchedSheetPins.set(parentDesign, matchedPins);
                const childNetIndex = netIndexes.get(childDesign);
                for (const portNet of childPort.nets) {
                    const childNetName = String(portNet.name);
                    const childNet = childNetIndex?.get(childNetName);
                    if (!childNet) {
                        recordHierarchyWarning(project, KicadHierarchyProcessingResultType.MissingChildNet, drawing.name, pinName, `child port net "${childNetName}"`);
                        continue;
                    }
                    unionNets(parent, parentNet, childNet);
                }
            }
        }
    }
    new KicadBusHierarchyResolver(project, parent, netIndexes).resolve();
    const groups = groupNets(parent);
    const initialGroupWinners = getGroupWinners(groups);
    removeSheetPinDrivers(netIndexes);
    recordUnmatchedSheetPinDrivers(unmatchedSheetPins, matchedSheetPins, netIndexes, initialGroupWinners);
    const groupWinners = getGroupWinners(groups);
    const renamedByDesign = new Map();
    for (const group of groups.values()) {
        const winner = groupWinners.get(group[0]);
        if (!winner) {
            continue;
        }
        for (const net of group) {
            const owner = netOwners.get(net);
            if (!owner) {
                continue;
            }
            const renamed = renamedByDesign.get(owner) ?? new Map();
            renamed.set(String(net.name), winner.label);
            renamedByDesign.set(owner, renamed);
            if ((0, sigma_engine_shared_1.isNetRef)(net.name)) {
                net.name.setName(winner.label);
            }
        }
    }
    for (const [design, renamed] of renamedByDesign) {
        rekeyDesignNetReferences(design, renamed);
    }
}
function getNetSheetPath(net) {
    return netDrivers.get(net)?.[0]?.sheetPath ?? '/';
}
function recordUnmatchedSheetPinDrivers(unmatchedPins, matchedPinsByOwner, netIndexes, groupWinners) {
    const candidatesByOwner = new Map();
    for (const unmatchedPin of unmatchedPins) {
        const winner = groupWinners.get(unmatchedPin.net);
        if (winner && winner.priority > KicadNetNamingStrategy_1.KicadNetNamingStrategy.getLabelPriority('sheet_pin')) {
            continue;
        }
        if (!netDrivers.get(unmatchedPin.net)?.length) {
            continue;
        }
        const baseName = unmatchedPin.sheetPath + KicadNetNamingStrategy_1.KicadNetNamingStrategy.escapeNetName(unmatchedPin.pinName);
        const candidatesByName = candidatesByOwner.get(unmatchedPin.owner) ??
            new Map();
        const candidatesByNet = candidatesByName.get(baseName) ?? new Map();
        candidatesByNet.set(unmatchedPin.net, unmatchedPin);
        candidatesByName.set(baseName, candidatesByNet);
        candidatesByOwner.set(unmatchedPin.owner, candidatesByName);
    }
    for (const [owner, candidatesByName] of candidatesByOwner) {
        const netIndex = netIndexes.get(owner);
        if (!netIndex) {
            continue;
        }
        const nameIndex = getSheetPinNameIndex(netIndex, candidatesByName, matchedPinsByOwner.get(owner) ?? [], groupWinners);
        for (const [baseName, candidatesByNet] of candidatesByName) {
            if (candidatesByNet.size !== 1) {
                continue;
            }
            const [unmatchedPin] = candidatesByNet.values();
            if (!unmatchedPin) {
                continue;
            }
            const labelOwners = nameIndex.labelOwners.get(baseName);
            const matchedSheetPinOwners = nameIndex.matchedSheetPinOwners.get(baseName);
            const hasOtherEligibleOwner = (labelOwners ? [...labelOwners].some((net) => net !== unmatchedPin.net) : false) ||
                (matchedSheetPinOwners ? [...matchedSheetPinOwners].some((net) => net !== unmatchedPin.net) : false);
            if (hasOtherEligibleOwner) {
                const suffixedName = getFirstAvailableSheetPinSuffix(baseName, nameIndex.reservedNames);
                recordUnmatchedSheetPinDriver(unmatchedPin, suffixedName);
                nameIndex.reservedNames.add(suffixedName);
                continue;
            }
            const groupWinnerOwners = nameIndex.groupWinnerOwners.get(baseName);
            if (groupWinnerOwners && [...groupWinnerOwners].some((net) => net !== unmatchedPin.net)) {
                continue;
            }
            recordUnmatchedSheetPinDriver(unmatchedPin, baseName);
            nameIndex.reservedNames.add(baseName);
        }
    }
}
function recordUnmatchedSheetPin(unmatchedPins, parentNetName, parentNetIndex, pinName, owner) {
    const parentNet = parentNetName ? parentNetIndex.get(String(parentNetName)) : undefined;
    if (!parentNet) {
        return;
    }
    unmatchedPins.push({
        net: parentNet,
        pinName,
        sheetPath: getNetSheetPath(parentNet),
        owner,
    });
}
function getSheetPinNameIndex(netIndex, candidatesByName, matchedPins, groupWinners) {
    const reservedNames = new Set(candidatesByName.keys());
    const labelOwners = new Map();
    const matchedSheetPinOwners = new Map();
    const groupWinnerOwners = new Map();
    for (const net of netIndex.values()) {
        reservedNames.add(String(net.name));
        const winnerName = groupWinners.get(net)?.label;
        if (winnerName) {
            reservedNames.add(winnerName);
            addSheetPinNameOwner(groupWinnerOwners, winnerName, net);
        }
        for (const candidate of netDrivers.get(net) ?? []) {
            reservedNames.add(candidate.label);
            if (!isLabelDriver(candidate)) {
                continue;
            }
            addSheetPinNameOwner(labelOwners, candidate.label, net);
            if (candidate.type === 'local_label') {
                const pathQualifiedName = candidate.sheetPath + KicadNetNamingStrategy_1.KicadNetNamingStrategy.escapeNetName(candidate.label);
                reservedNames.add(pathQualifiedName);
                addSheetPinNameOwner(labelOwners, pathQualifiedName, net);
            }
        }
    }
    for (const matchedPin of matchedPins) {
        if (!matchedPin.isSoleSheetPinDriver) {
            continue;
        }
        const hasStrongerParentDriver = netDrivers
            .get(matchedPin.net)
            ?.some((candidate) => candidate.priority > KicadNetNamingStrategy_1.KicadNetNamingStrategy.getLabelPriority('sheet_pin'));
        if (hasStrongerParentDriver) {
            continue;
        }
        reservedNames.add(matchedPin.name);
        addSheetPinNameOwner(matchedSheetPinOwners, matchedPin.name, matchedPin.net);
    }
    return { reservedNames, labelOwners, matchedSheetPinOwners, groupWinnerOwners };
}
function addSheetPinNameOwner(owners, name, net) {
    const namedOwners = owners.get(name) ?? new Set();
    namedOwners.add(net);
    owners.set(name, namedOwners);
}
function isLabelDriver(candidate) {
    return candidate.type === 'local_label' || candidate.type === 'hier_label';
}
function getFirstAvailableSheetPinSuffix(baseName, reservedNames) {
    let suffix = 1;
    while (reservedNames.has(`${baseName}_${suffix}`)) {
        suffix += 1;
    }
    return `${baseName}_${suffix}`;
}
function removeSheetPinDrivers(netIndexes) {
    for (const netIndex of netIndexes.values()) {
        for (const net of netIndex.values()) {
            const candidates = netDrivers.get(net);
            if (candidates) {
                netDrivers.set(net, candidates.filter((candidate) => candidate.type !== 'sheet_pin'));
            }
        }
    }
}
function recordUnmatchedSheetPinDriver(unmatchedPin, label) {
    const candidates = netDrivers.get(unmatchedPin.net);
    if (!candidates) {
        return;
    }
    candidates.push({
        label,
        priority: KicadNetNamingStrategy_1.KicadNetNamingStrategy.getLabelPriority('sheet_pin'),
        type: 'sheet_pin',
        hierarchyDepth: unmatchedPin.sheetPath.split('/').filter(Boolean).length,
        sheetPath: unmatchedPin.sheetPath,
    });
}
function isSchematicDesign(design) {
    return design?.modelType === 'schematic';
}
function indexDesignNets(design) {
    const netIndex = new Map();
    for (const net of (0, RefableNet_1.refableObjectValues)(design.nets)) {
        netIndex.set(String(net.name), net);
    }
    return netIndex;
}
function findNet(parent, net) {
    const current = parent.get(net);
    if (!current || current === net) {
        return net;
    }
    const root = findNet(parent, current);
    parent.set(net, root);
    return root;
}
function unionNets(parent, first, second) {
    if (!parent.has(first)) {
        parent.set(first, first);
    }
    if (!parent.has(second)) {
        parent.set(second, second);
    }
    const firstRoot = findNet(parent, first);
    const secondRoot = findNet(parent, second);
    if (firstRoot !== secondRoot) {
        parent.set(secondRoot, firstRoot);
    }
}
function groupNets(parent) {
    const groups = new Map();
    for (const net of parent.keys()) {
        const root = findNet(parent, net);
        const group = groups.get(root) ?? [];
        group.push(net);
        groups.set(root, group);
    }
    return groups;
}
function getGroupWinners(groups) {
    const winners = new Map();
    for (const group of groups.values()) {
        const winner = group.flatMap((net) => netDrivers.get(net) ?? []).sort(compareHierarchyDrivers)[0];
        for (const net of group) {
            winners.set(net, winner);
        }
    }
    return winners;
}
function compareHierarchyDrivers(first, second) {
    if (first.priority !== second.priority) {
        return second.priority - first.priority;
    }
    if (first.hierarchyDepth !== second.hierarchyDepth) {
        return first.hierarchyDepth - second.hierarchyDepth;
    }
    return KicadNetNamingStrategy_1.KicadNetNamingStrategy.compareDriverCandidates(first, second);
}
function recordHierarchyWarning(project, type, sheetName, pinName, missing) {
    project.processingResults.innerResults.push({
        type,
        severity: sigma_engine_shared_1.BoundaryLevel.Warning,
        message: `KiCad hierarchy could not resolve ${missing} for sheet "${sheetName}" pin "${pinName}".`,
    });
}
function recordHierarchySheetWarning(project, type, sheetName, missing) {
    project.processingResults.innerResults.push({
        type,
        severity: sigma_engine_shared_1.BoundaryLevel.Warning,
        message: `KiCad hierarchy could not resolve ${missing} for sheet "${sheetName}".`,
    });
}
function hasDrawingNetReferences(drawing) {
    return 'net' in drawing || 'nets' in drawing;
}
function rekeyDesignNetReferences(design, renamed) {
    for (const module of design.modules) {
        for (const [pinRef, netName] of Object.entries(module.pinNets)) {
            module.pinNets[pinRef] = rekeyNetReference(netName, renamed);
        }
    }
    for (const port of Object.values(design.ports)) {
        for (const portNet of port.nets) {
            portNet.name = rekeyNetReference(portNet.name, renamed);
        }
    }
    for (const drawing of Object.values(design.drawings).flat()) {
        rekeyDrawingNetReferences(drawing, renamed);
    }
    for (const net of (0, RefableNet_1.refableObjectValues)(design.nets)) {
        if (!net.diffPair) {
            continue;
        }
        if (typeof net.diffPair.positiveId === 'string') {
            net.diffPair.positiveId = rekeyNetReference(net.diffPair.positiveId, renamed);
        }
        if (typeof net.diffPair.negativeId === 'string') {
            net.diffPair.negativeId = rekeyNetReference(net.diffPair.negativeId, renamed);
        }
    }
}
function rekeyNetReference(name, renamed) {
    const currentName = String(name);
    return renamed.get(currentName) ?? currentName;
}
function rekeyDrawingNetReferences(drawing, renamed) {
    if (hasDrawingNetReferences(drawing)) {
        if (drawing.net !== undefined) {
            drawing.net = rekeyNetReference(drawing.net, renamed);
        }
        for (const pointer of drawing.nets ?? []) {
            pointer.n = rekeyNetReference(pointer.n, renamed);
        }
    }
    if (drawing.type === 'group') {
        for (const child of drawing.drawings) {
            rekeyDrawingNetReferences(child, renamed);
        }
    }
}
//# sourceMappingURL=KicadHierarchyResolver.js.map